import React from 'react';
import { flexRender } from '@tanstack/react-table';
import SortIcon from '@common/components/icons/SortIcon';
import SortAscIcon from '@common/components/icons/SortAscIcon';
import SortDescIcon from '@common/components/icons/SortDescIcon';
import * as styles from './index.module.css';

export const HeaderCell = React.memo(
  ({
    header,
    cellClassName,
    sorting,
    onSortingChange,
    isResizing,
    setIsResizing,
  }) => {
    const isSortable = header.column.getCanSort?.() !== false;
    const isSorted = sorting.length > 0 && sorting[0]?.id === header.column.id;
    const isCheckbox = header.column.id === 'select';
    const isActions = header.column.id === 'actions';

    if (isCheckbox || isActions) {
      return (
        <th key={header.id}>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </th>
      );
    }

    // Prevent sorting after resizing
    let resizeHandler;
    if (header.column.getCanResize()) {
      const handler = header.getResizeHandler();
      resizeHandler = (e) => {
        setIsResizing(true);
        handler(e);
      };
    }

    const handleClick = () => {
      if (!isSortable) return;
      if (isResizing) {
        setIsResizing(false);
        return;
      }
      if (onSortingChange) {
        const newSorting =
          sorting.length > 0 && sorting[0].id === header.column.id
            ? [{ id: header.column.id, desc: !sorting[0].desc }]
            : [{ id: header.column.id, desc: false }];
        onSortingChange(newSorting);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    };

    const renderSortIcon = () => {
      if (!isSorted) {
        return (
          <SortIcon
            className={`${styles['header-sort-icon']} ${styles.inline} ${styles.opacity30}`}
          />
        );
      }

      if (sorting[0].desc) {
        return (
          <SortAscIcon
            className={`${styles['header-sort-icon']} ${styles.inline}`}
          />
        );
      }

      return (
        <SortDescIcon
          className={`${styles['header-sort-icon']} ${styles.inline}`}
        />
      );
    };

    const renderHeaderContent = () => (
      <span className={styles['header-content']}>
        <span className={styles['header-text']}>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </span>
        {isSortable && (
          <span className={styles['header-sort-icon']}>{renderSortIcon()}</span>
        )}
      </span>
    );

    if (!isSortable) {
      return (
        <th key={header.id} className={cellClassName || undefined}>
          {renderHeaderContent()}
          {header.column.getCanResize() && (
            <div
              role="button"
              aria-label="Resize column"
              tabIndex={0}
              onMouseDown={resizeHandler}
              onTouchStart={resizeHandler}
              className={styles['resize-handler']}
            />
          )}
        </th>
      );
    }

    return (
      <th
        key={header.id}
        className={cellClassName || undefined}
        role="button"
        aria-label="Header cell"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {renderHeaderContent()}
        {header.column.getCanResize() && (
          <div
            onMouseDown={resizeHandler}
            onTouchStart={resizeHandler}
            className={styles['resize-handler']}
            role="button"
            aria-label="Resize column"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                resizeHandler(e);
              }
            }}
          />
        )}
      </th>
    );
  },
);

